/**
 * CalendarPicker - Custom Calendar Date Picker
 *
 * A custom calendar picker that allows month navigation without closing.
 * Unlike native <input type="date">, this component:
 * - Stays open while navigating between months
 * - Only closes when a date is explicitly selected
 * - Provides visual feedback for selected date and today
 * - Supports keyboard navigation
 * - Uses React Portal to render dropdown outside parent overflow constraints
 * - Supports keyboard date input (MM/DD/YYYY format)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar, Keyboard } from 'lucide-react';
import { cn } from '../../utils/cn';

interface CalendarPickerProps {
  /** Currently selected date (YYYY-MM-DD format) */
  value: string;
  /** Callback when date is selected */
  onChange: (date: string) => void;
  /** Callback when picker is closed without selection */
  onClose?: () => void;
  /** Optional className for the trigger button */
  className?: string;
  /** Placeholder when no date selected */
  placeholder?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Show keyboard input field for typing MM/DD/YYYY */
  showKeyboardInput?: boolean;
}

/**
 * Parse MM/DD/YYYY or M/D/YYYY format to YYYY-MM-DD
 * Returns null if invalid
 */
const parseMMDDYYYY = (input: string): string | null => {
  // Remove any whitespace
  const cleaned = input.trim();

  // Match MM/DD/YYYY or M/D/YYYY patterns
  const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Validate ranges
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;

  // Additional validation: check if the date is actually valid
  const testDate = new Date(year, month - 1, day);
  if (testDate.getMonth() !== month - 1 || testDate.getDate() !== day) {
    return null; // Invalid date like 02/30/2024
  }

  // Format as YYYY-MM-DD
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Format YYYY-MM-DD to MM/DD/YYYY for keyboard input display
 */
const formatToMMDDYYYY = (dateStr: string): string => {
  if (!dateStr) return '';
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  return `${match[2]}/${match[3]}/${match[1]}`;
};

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Get number of days in a month
 */
const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get the day of week for the first day of a month (0 = Sunday)
 */
const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

/**
 * Format date for display
 */
const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format date as YYYY-MM-DD
 */
const formatDateValue = (year: number, month: number, day: number): string => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  value,
  onChange,
  onClose,
  className,
  placeholder = 'Select date',
  disabled = false,
  showKeyboardInput = true, // Enable keyboard input by default
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; dropUp: boolean } | null>(null);
  const [keyboardInputValue, setKeyboardInputValue] = useState(formatToMMDDYYYY(value));
  const [keyboardInputError, setKeyboardInputError] = useState<string | null>(null);
  // Pending date for staged selection (user must click Apply or press Enter)
  const [pendingDate, setPendingDate] = useState<string | null>(value || null);

  // Parse the current value to determine initial view month/year
  const parsedDate = value ? new Date(value + 'T00:00:00') : new Date();
  const [viewMonth, setViewMonth] = useState(parsedDate.getMonth());
  const [viewYear, setViewYear] = useState(parsedDate.getFullYear());

  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const keyboardInputRef = useRef<HTMLInputElement>(null);

  // Sync keyboard input and pending date when value prop changes externally
  useEffect(() => {
    setKeyboardInputValue(formatToMMDDYYYY(value));
    setKeyboardInputError(null);
    setPendingDate(value || null);
  }, [value]);

  // Reset pending date when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setPendingDate(value || null);
      setKeyboardInputValue(formatToMMDDYYYY(value));
    }
  }, [isOpen, value]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 380; // Approximate height of calendar dropdown
      const dropdownWidth = 280;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Check if there's room below, otherwise drop up
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      // Calculate left position, keep within viewport
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - 16) {
        left = viewportWidth - dropdownWidth - 16;
      }
      if (left < 16) {
        left = 16;
      }

      setDropdownPosition({
        top: dropUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left,
        dropUp,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both the container and the portal dropdown
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideCalendar = calendarRef.current && !calendarRef.current.contains(target);

      if (isOutsideContainer && isOutsideCalendar) {
        setIsOpen(false);
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Reset view to selected date when opening
  useEffect(() => {
    if (isOpen && value) {
      const date = new Date(value + 'T00:00:00');
      setViewMonth(date.getMonth());
      setViewYear(date.getFullYear());
    }
  }, [isOpen, value]);

  // Navigate to previous month
  const handlePrevMonth = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  }, [viewMonth, viewYear]);

  // Navigate to next month
  const handleNextMonth = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  }, [viewMonth, viewYear]);

  // Handle date selection - sets pending date (user must click Apply)
  const handleSelectDate = useCallback((day: number) => {
    const dateStr = formatDateValue(viewYear, viewMonth, day);
    setPendingDate(dateStr);
    setKeyboardInputValue(formatToMMDDYYYY(dateStr));
    setKeyboardInputError(null);
  }, [viewYear, viewMonth]);

  // Apply the pending date and close dropdown
  const handleApply = useCallback(() => {
    if (pendingDate) {
      onChange(pendingDate);
    }
    setIsOpen(false);
  }, [pendingDate, onChange]);

  // Clear the date
  const handleClear = useCallback(() => {
    setPendingDate(null);
    setKeyboardInputValue('');
    setKeyboardInputError(null);
    onChange('');
    setIsOpen(false);
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      onClose?.();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  }, [onClose, handleApply]);

  // Handle keyboard date input change
  const handleKeyboardInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setKeyboardInputValue(inputValue);
    setKeyboardInputError(null);

    // Try to parse as user types (but don't apply until they press Enter/Apply)
    if (inputValue.length === 10) {
      const parsed = parseMMDDYYYY(inputValue);
      if (parsed) {
        setPendingDate(parsed);
        // Update view to show the selected month
        const date = new Date(parsed + 'T00:00:00');
        setViewMonth(date.getMonth());
        setViewYear(date.getFullYear());
      }
    }
  }, []);

  // Handle keyboard input submit (Enter) - validates and sets pending date
  const handleKeyboardInputSubmit = useCallback(() => {
    if (!keyboardInputValue.trim()) {
      setKeyboardInputError(null);
      return;
    }

    const parsed = parseMMDDYYYY(keyboardInputValue);
    if (parsed) {
      setPendingDate(parsed);
      setKeyboardInputError(null);
      // Update view to show the selected month
      const date = new Date(parsed + 'T00:00:00');
      setViewMonth(date.getMonth());
      setViewYear(date.getFullYear());
      // Apply immediately when typing + Enter
      onChange(parsed);
      setIsOpen(false);
    } else {
      setKeyboardInputError('Invalid date. Use MM/DD/YYYY format.');
    }
  }, [keyboardInputValue, onChange]);

  // Handle keyboard input keydown
  const handleKeyboardInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleKeyboardInputSubmit();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      onClose?.();
    }
  }, [handleKeyboardInputSubmit, onClose]);

  // Build calendar grid
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDayOfMonth = getFirstDayOfMonth(viewYear, viewMonth);

  const today = new Date();
  const todayStr = formatDateValue(today.getFullYear(), today.getMonth(), today.getDate());

  // Create calendar days array
  const calendarDays: (number | null)[] = [];

  // Add empty cells for days before first of month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }

  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Render calendar dropdown content
  const calendarContent = (
    <div
      ref={calendarRef}
      className={cn(
        'fixed p-3 bg-dark-surface border border-dark-border/30',
        'rounded-xl shadow-2xl shadow-black/30 min-w-[280px]'
      )}
      style={{
        top: dropdownPosition?.top ?? 0,
        left: dropdownPosition?.left ?? 0,
        zIndex: 99999, // Above modals (z-50) to prevent clipping
      }}
      data-testid="calendar-picker-dropdown"
    >
      {/* Month/Year Header with Navigation */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={handlePrevMonth}
          className={cn(
            'p-1.5 rounded-lg hover:bg-dark-bg transition-colors',
            'text-dark-muted hover:text-dark-text'
          )}
          aria-label="Previous month"
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <span className="text-sm font-medium text-dark-text">
          {MONTHS[viewMonth]} {viewYear}
        </span>

        <button
          type="button"
          onClick={handleNextMonth}
          className={cn(
            'p-1.5 rounded-lg hover:bg-dark-bg transition-colors',
            'text-dark-muted hover:text-dark-text'
          )}
          aria-label="Next month"
          data-testid="calendar-next-month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Days of Week Header */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS_OF_WEEK.map(day => (
          <div
            key={day}
            className="text-center text-xs text-dark-muted font-medium py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="p-2" />;
          }

          const dateStr = formatDateValue(viewYear, viewMonth, day);
          const isSelected = dateStr === pendingDate;
          const isToday = dateStr === todayStr;

          return (
            <button
              key={day}
              type="button"
              onClick={() => handleSelectDate(day)}
              className={cn(
                'p-2 text-sm rounded-lg transition-all duration-150',
                'hover:bg-brand-500/20 hover:text-brand-400',
                isSelected && 'bg-brand-500 text-white hover:bg-brand-600 hover:text-white',
                isToday && !isSelected && 'ring-1 ring-brand-400 text-brand-400',
                !isSelected && !isToday && 'text-dark-text'
              )}
              data-testid={`calendar-day-${day}`}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Keyboard Input Section */}
      {showKeyboardInput && (
        <div className="mt-3 pt-3 border-t border-dark-border/30">
          <label className="block text-[10px] text-dark-muted uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Keyboard className="w-3 h-3" />
            Type date (MM/DD/YYYY)
          </label>
          <input
            ref={keyboardInputRef}
            type="text"
            value={keyboardInputValue}
            onChange={handleKeyboardInputChange}
            onKeyDown={handleKeyboardInputKeyDown}
            onBlur={handleKeyboardInputSubmit}
            placeholder="MM/DD/YYYY"
            className={cn(
              'w-full px-2 py-1.5 text-sm rounded-lg',
              'bg-dark-bg border text-dark-text placeholder-dark-muted',
              'focus:outline-none focus:ring-2 focus:ring-brand-400',
              'transition-colors',
              keyboardInputError
                ? 'border-red-500/50 focus:ring-red-400'
                : 'border-dark-border/30'
            )}
            data-testid="calendar-keyboard-input"
          />
          {keyboardInputError && (
            <p className="text-[10px] text-red-400 mt-1">{keyboardInputError}</p>
          )}
          <p className="text-[10px] text-dark-muted mt-1">Press Enter to apply</p>
        </div>
      )}

      {/* Today Button */}
      <div className="mt-3 pt-3 border-t border-dark-border/30">
        <button
          type="button"
          onClick={() => {
            const today = new Date();
            setViewMonth(today.getMonth());
            setViewYear(today.getFullYear());
            handleSelectDate(today.getDate());
          }}
          className={cn(
            'w-full py-1.5 text-xs text-center rounded-lg',
            'bg-dark-bg hover:bg-dark-surface/50 text-dark-muted hover:text-dark-text',
            'transition-colors'
          )}
          data-testid="calendar-today-btn"
        >
          Today
        </button>
      </div>

      {/* Selected Preview */}
      {pendingDate && pendingDate !== value && (
        <div className="mt-2 px-2 py-1.5 bg-brand-500/10 border border-brand-500/30 rounded-lg">
          <p className="text-xs text-brand-400">
            Selected: <span className="font-medium">{formatDateDisplay(pendingDate)}</span>
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-dark-border/30">
        {/* Clear button */}
        {(pendingDate || value) && (
          <button
            type="button"
            onClick={handleClear}
            data-testid="calendar-clear-btn"
            className={cn(
              'flex-1 px-3 py-2 text-sm text-dark-muted',
              'hover:text-red-400 hover:bg-red-500/10',
              'rounded-md transition-all duration-150 min-h-[40px]'
            )}
          >
            Clear
          </button>
        )}

        {/* Apply button */}
        <button
          type="button"
          onClick={handleApply}
          disabled={!pendingDate}
          data-testid="calendar-apply-btn"
          className={cn(
            'flex-1 px-3 py-2 text-sm font-medium rounded-md',
            'transition-all duration-150 min-h-[40px]',
            pendingDate
              ? 'bg-brand-500 hover:bg-brand-600 text-white'
              : 'bg-dark-bg text-dark-muted cursor-not-allowed'
          )}
        >
          Apply (Enter)
        </button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full text-left px-2 py-1 text-sm rounded cursor-pointer',
          'hover:bg-dark-surface/50 transition-colors',
          'flex items-center gap-1 h-7 box-border',
          value ? 'text-dark-text' : 'text-dark-muted italic',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        data-testid="calendar-picker-trigger"
      >
        {value ? formatDateDisplay(value) : placeholder}
      </button>

      {/* Calendar Dropdown - rendered in portal to escape overflow constraints */}
      {isOpen && dropdownPosition && createPortal(calendarContent, document.body)}
    </div>
  );
};

export default CalendarPicker;
